import { MAX_ATTACHMENT_SIZE } from '../utils/attachmentLimits';
import * as Yup from 'yup';

/**
 * Validation schema for child information form.
 * Uses Yup for form validation.
 * @type {Yup.ObjectSchema}
 */

export const personalInformationValidationSchema = Yup.object().shape({
  surname: Yup.string()
    .required('Surname is required').strict()
    .matches(/^[A-Za-zÑñ\s.'-]{1,50}$/, 'Surname contains invalid characters'),
  firstName: Yup.string()
    .required('First name is required').strict()
    .matches(/^[A-Za-zÑñ\s.'-]{1,50}$/, 'First name contains invalid characters'),
  middleName: Yup.string()
    .strict()
    .nullable()
    .matches(/^[A-Za-zÑñ\s.'-]{0,50}$/, "Middle name contains invalid characters")
    .max(50, "Middle name should not exceed 50 characters"),
  childBirthday: Yup.date()
    .nullable()
    .required(`Child's birthday is required`)
    .typeError(`Child's Birthday must be a valid date`)
    .test('below-18', 'Child must be below 18 years old', function (value) {
      if (!value) return false; // If no value, considered invalid
      const today = new Date();
      const age = today.getFullYear() - value.getFullYear();
      const monthDiff = today.getMonth() - value.getMonth();
      // If birthday hasn't occurred yet this year, subtract 1 from age
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < value.getDate())) {
        return age - 1 < 18; // Birthday hasn't happened yet, actual age is age - 1
      }
      return age < 18; // Birthday has already happened this year
    }),
  gender: Yup.string()
    .required(`Gender is required`),
  religion: Yup.string()
    .required(`Religion is required`),
  bloodType: Yup.string()
    .nullable(), // Blood type is now optional
  houseNo: Yup.string()
    .required(`House # is required`),
  blkAndLot: Yup.string()
    .nullable(), // Blk & Lot is now optional
  street: Yup.string()
    .required(`Street is required`),
  barangay: Yup.string()
    .required(`Barangay is required`),
  district: Yup.string()
    .required(`District is required`),
  city: Yup.string()
    .required(`City is required`),
  height: Yup.number()
    .required('Ang height ay kailangan')
    .min(20, 'Ang height ay dapat hindi bababa sa 20cm')
    .max(300, 'Ang height ay dapat hindi hihigit sa 300cm')
    .test('decimal-places', 'Hanggang dalawang decimal places lang ang pwede', 
      (value) => {
        if (!value) return true;
        const decimalPlaces = value.toString().split('.')[1]?.length || 0;
        return decimalPlaces <= 2;
      }),
  weight: Yup.number()
    .required('Ang weight ay kailangan')
    .min(2, 'Ang weight ay dapat hindi bababa sa 2kg')
    .max(150, 'Ang weight ay dapat hindi hihigit sa 150kg')
    .test('decimal-places', 'Hanggang dalawang decimal places lang ang pwede', 
      (value) => {
        if (!value) return true;
        const decimalPlaces = value.toString().split('.')[1]?.length || 0;
        return decimalPlaces <= 2;
      }),


  qcId: Yup.string()
    .nullable()
    .notRequired()
    .test('qcid-format', 'QCID number must be 14 to 16 digits when provided.', function(value) {
      if (!value || value.trim() === '') {
        return true; // Empty values are valid for optional field
      }
      const cleanValue = value.trim().replace(/\D/g, '');
      return /^[0-9]{14,16}$/.test(cleanValue);
    }),

  // education
  educationalStatus: Yup.string()
    .required('Education status is required'),
  educationLevel: Yup.string()
    .when('educationalStatus', (educationalStatus, schema) => {
      const eduStatus = Array.isArray(educationalStatus) ? educationalStatus[0] : educationalStatus;
      return eduStatus == 'studying'
        ? schema.required('Education level is required')
        : schema;
    }),
  schoolName: Yup.string()
    .when('educationalStatus', (educationalStatus, schema) => {
      const eduStatus = Array.isArray(educationalStatus) ? educationalStatus[0] : educationalStatus;
      return eduStatus == 'studying'
        ? schema.required('School name is required')
        : schema;
    }),
  specificSchoolName: Yup.string()
    .when('schoolName', (schoolName, schema) => {
      const school = Array.isArray(schoolName) ? schoolName[0] : schoolName;
      return school == 'IbaPa'
        ? schema.required('Specific school name is required')
        : schema;
    }),
  gradeLevel: Yup.string()
    .when('educationalStatus', (educationalStatus, schema) => {
      const eduStatus = Array.isArray(educationalStatus) ? educationalStatus[0] : educationalStatus;
      return eduStatus == 'studying'
        ? schema.required('Grade level is required')
        : schema;
    }),
  lastYearOfStudy: Yup.string()
    .when('educationalStatus', (educationalStatus, schema) => {
      const eduStatus = Array.isArray(educationalStatus) ? educationalStatus[0] : educationalStatus;
      return eduStatus == 'stopped'
        ? schema.required('Last year of studying is required')
        : schema;
    }),
  reasonForNotStudying: Yup.array()
    .when('educationalStatus', (educationalStatus, schema) => {
      const eduStatus = Array.isArray(educationalStatus) ? educationalStatus[0] : educationalStatus;
      return eduStatus == 'never_attended_school'
        ? schema.required('Reason for not studying is required')
          .min(1, 'At least one reason must be selected')
        : schema;
    }),

  hasMenstruationStarted: Yup.string()
    .required('This is required'),
  isCircumcised: Yup.string()
    .required('This is required'),
  isInterestedInArts: Yup.string()
    .required('This is required'),
  isSkilledInHouseholdChores: Yup.string()
    .required('This is required'),

  householdChoresChildCanDo: Yup.array()
    .when('isSkilledInHouseholdChores', (isSkilled, schema) => {
      const dep = Array.isArray(isSkilled) ? isSkilled[0] : isSkilled;
      return dep == 'Oo'
        ? schema
          .min(1, 'Please select at least one household chore')
          .required('Please select the household chores the child can do')
        : schema.nullable();
    }),

  // Certificate of Disability
  certificateOfDisability: Yup.string()
    .required('This is required'),
  lastCheckUp: Yup.string()
    .when('certificateOfDisability', (value, schema) => {
      const dep = Array.isArray(value) ? value[0] : value;
      return dep == 'Meron'
        ? schema.required('When was the last checkup is required')
        : schema;
    }),
  certificateOfDisabilityImage: Yup.mixed()
    .when('certificateOfDisability', (value, schema) => {
      const dep = Array.isArray(value) ? value[0] : value;
      return dep === 'Meron'
        ? schema
          .required('Upload image is required')
          .test(
            'fileSize',
            "The file you're trying to upload is too large. Please make sure it's 10MB or smaller.",
            (value) => {
              // Allow if value is string (already uploaded file path)
              if (typeof value === 'string') return true;
              // Check file size for new uploads
              return value && value.size <= MAX_ATTACHMENT_SIZE; // 10MB
            }
          )
          .test(
            'fileType',
            'Invalid file format. Only image files (.jpg, .jpeg, .png, .heif, .heic, .hei) are allowed.',
            (value) => {
              // Allow if value is string (already uploaded file path from draft/restore)
              if (typeof value === 'string') return true;
              
              if (!value) return false;
              
              // Validate by file extension (more reliable than MIME type for HEIF/HEIC)
              const fileName = value.name ? value.name.toLowerCase() : '';
              const validExtensions = ['.jpg', '.jpeg', '.png', '.heif', '.heic', '.hei'];
              const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
              
              // Also check MIME type as backup validation
              const validMimeTypes = [
                'image/jpeg', 
                'image/png', 
                'image/heif', 
                'image/heic',
                'image/heif-sequence',
                'image/heic-sequence',
                'image/x-heic'
              ];
              const hasValidMimeType = value.type && validMimeTypes.includes(value.type);
              
              // Accept if either extension or MIME type is valid (HEIC MIME types vary by browser)
              return hasValidExtension || hasValidMimeType;
            }
          )
        : schema;
    }),


  // PWD ID
  pwdIdStatus: Yup.string()
    .required('This is required'),
  pwdIdNumber: Yup.number()
    .transform((value, originalValue) => {
      // Transform empty string to null for optional number field
      return originalValue === '' ? null : value;
    })
    .nullable()
    .typeError('PWD ID must be a valid number')
    .positive('PWD ID must be a positive number')
    .integer('PWD ID must be a whole number')
    .when('pwdIdStatus', (value, schema) => {
      const dep = Array.isArray(value) ? value[0] : value;
      return dep == 'Meron'
        ? schema.required('PWD ID is required')
        : schema;
    }),

  // Parent's Validation
  parentSurname: Yup.string()
    .required('Surname is required').strict(),
  parentFirstName: Yup.string()
    .required('First name is required').strict(),
  parentBirthday: Yup.date()
    .nullable()
    .required(`Parent's birthday is required`)
    .typeError(`Parent's Birthday must be a valid date`),
  parentGender: Yup.string()
    .required(`Gender is required`),
  parentReligion: Yup.string()
    .required(`Religion is required`),
  parentBloodType: Yup.string()
    .nullable(), // Parent blood type is now optional
  parentHeight: Yup.number()
    .transform((value, originalValue) => {
      // Transform empty string to null for optional number field
      return originalValue === '' ? null : value;
    })
    .nullable()
    .typeError('Parent height must be a valid number')
    .positive('Parent height must be greater than 0')
    .min(20, 'Height must be at least 20cm')
    .max(300, 'Height must not exceed 300cm')
    .test('decimal-places', 'Maximum 2 decimal places allowed', 
      (value) => {
        if (!value) return true;
        const decimalPlaces = value.toString().split('.')[1]?.length || 0;
        return decimalPlaces <= 2;
      }),
  parentWeight: Yup.number()
    .transform((value, originalValue) => {
      // Transform empty string to null for optional number field
      return originalValue === '' ? null : value;
    })
    .nullable()
    .typeError('Parent weight must be a valid number')
    .positive('Parent weight must be greater than 0')
    .min(2, 'Weight must be at least 2kg')
    .max(150, 'Weight must not exceed 150kg')
    .test('decimal-places', 'Maximum 2 decimal places allowed',
      (value) => {
        if (!value) return true;
        const decimalPlaces = value.toString().split('.')[1]?.length || 0;
        return decimalPlaces <= 2;
      }),
  parentCivilStatus: Yup.string()
    .required(`Civil Status is required`),
  parentEducationalAttainment: Yup.string()
    .required(`Educational Attainment is required`),
  parentSpecificEducationalAttainment: Yup.string()
    .when('parentEducationalAttainment', (value, schema) => {
      const dep = Array.isArray(value) ? value[0] : value;
      return dep == 'Iba pa'
        ? schema.required(`Specific Educational Attainment is required`)
        : schema;
    }),
  parentEmailAddress: Yup.string()
    .email('Invalid email address')
    .required(`Email address is required`),
  parentCellphone: Yup.string()
    .required(`Cellphone number is required`)
    .matches(
      /^(09\d{9}|09\d{2}\s\d{3}\s\d{4})$/,
      "Mobile number must start with 09 followed by 9 digits"
    ),
  parentFacebook: Yup.string()
    .required(`Facebook is required`),
  parentFacebookLink: Yup.string()
    .required('Facebook link is required')
    .test('valid-facebook-link', 'Facebook link must be a valid URL', (value) => {
      if (!value || value.trim() === '') return false;
      return /^https?:\/\/.+/i.test(value.trim());
    }),

  // Parent's PWD-related ORG
  parentPwdRelatedOrg: Yup.string()
    .required('This is required'),
  parentPwdRelatedOrgName: Yup.string()
    .when('parentPwdRelatedOrg', (parentPwdRelatedOrg, schema) => {
      const dep = Array.isArray(parentPwdRelatedOrg) ? parentPwdRelatedOrg[0] : parentPwdRelatedOrg;
      return dep == 'Oo'
        ? schema.required(`PWD-related organization's name is required`)
        : schema;
    }),

  // Parent's QCID
  parentQcId: Yup.string()
    .nullable()
    .notRequired()
    .test('qcid-format', 'QCID number must be 14 to 16 digits when provided.', function(value) {
      if (!value || value.trim() === '') {
        return true; // Empty values are valid for optional field
      }
      const cleanValue = value.trim().replace(/\D/g, '');
      return /^[0-9]{14,16}$/.test(cleanValue);
    }),

});

// Backward-compatible export names used by legacy form tests.
export const validationChildPersonalInformationSchema = personalInformationValidationSchema;
export const validationParentPersonalInformationSchema = personalInformationValidationSchema;